import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, Shield, ToggleLeft, ToggleRight } from 'lucide-react'

import { useLocalAuth } from '@/auth/LocalAuthContext'
import api from '@/services/api'
import TotpEnrolmentFlow from './TotpEnrolmentFlow'
import { enrolmentClient, errorDetail } from './totpClient'

type Status = { enabled: boolean; pending: boolean; required_by_policy: boolean; passkeys?: number }
type Mode = 'idle' | 'enrol' | 'rekey-code' | 'rekey' | 'disable'

/**
 * Settings > Security > Authenticator. Any local account can enrol itself here,
 * permanent or trial; nothing is enforced until a code from the app is confirmed.
 *
 * Replacing or removing an authenticator needs a code from the CURRENT one, so a
 * borrowed or stolen session cannot take the account's second factor over. A
 * replacement does not take effect until its first code is confirmed: until
 * then the authenticator the person has keeps answering sign-in. When the
 * platform requires two-factor sign-in it cannot be turned off here at all.
 *
 * A passkey registered on the account is a second factor too (see the Passkeys
 * panel beside this one); this panel says so rather than calling the account
 * unprotected.
 */
export default function TotpPanel() {
  const { isLocalAuth } = useLocalAuth()
  const client = useMemo(() => enrolmentClient(api), [])
  const [status, setStatus] = useState<Status | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [mode, setMode] = useState<Mode>('idle')
  const [currentCode, setCurrentCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<Status>('/auth/2fa/status')
      setStatus(res.data)
      setLoadFailed(false)
    } catch {
      setLoadFailed(true)
    }
  }, [])

  useEffect(() => {
    if (isLocalAuth) void refresh()
  }, [isLocalAuth, refresh])

  if (!isLocalAuth) {
    return (
      <p className="text-[11px] leading-relaxed text-stone-500">
        This session uses Microsoft sign-in. Two-factor authentication is managed by your Microsoft account,
        not on this platform.
      </p>
    )
  }
  if (loadFailed && !status) {
    return <p className="text-[11px] text-red-300">Could not read your two-factor status. Reload and try again.</p>
  }
  if (!status) return <p className="text-[11px] text-stone-500">Loading…</p>

  const back = () => {
    setMode('idle')
    setCurrentCode('')
    setError(null)
  }
  const finished = async (text: string) => {
    back()
    setMessage(text)
    await refresh()
  }

  const disable = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.post('/auth/2fa/disable', { code: currentCode })
      await finished('Two-factor authentication was turned off.')
    } catch (err) {
      setError(errorDetail(err, 'Could not turn two-factor off.'))
      setCurrentCode('')
    } finally {
      setBusy(false)
    }
  }

  const passkeys = status.passkeys ?? 0
  const headline = status.enabled
    ? 'Two-factor authentication is on for this account.'
    : status.pending
      ? 'Set-up was started but not finished, so two-factor is not protecting this account yet.'
      : 'Two-factor authentication is off.'

  const codeStep = (label: string, action: () => void, actionLabel: string) => (
    <div className="space-y-2">
      <input
        aria-label="Current authenticator code"
        autoComplete="one-time-code"
        inputMode="numeric"
        maxLength={6}
        placeholder="Current 6-digit code"
        className="w-full rounded border border-amber-400/15 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-400/40"
        value={currentCode}
        onChange={(event) => setCurrentCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
      />
      <p className="text-[10px] text-stone-500">{label}</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-primary text-[12px] disabled:opacity-50"
          disabled={busy || currentCode.length !== 6}
          onClick={action}
        >
          {actionLabel}
        </button>
        <button type="button" className="text-[11px] text-stone-500 hover:text-stone-300" onClick={back}>
          Cancel
        </button>
      </div>
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0 text-amber-300">
          {status.enabled ? <ToggleRight size={26} /> : <ToggleLeft size={26} className="text-stone-600" />}
        </div>
        <div>
          <div className="flex items-center gap-2 text-[12px] font-medium text-stone-200">
            <Shield size={14} /> Authenticator app (TOTP)
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-stone-500">{headline}</div>
        </div>
      </div>

      {status.required_by_policy && !status.enabled && passkeys > 0 ? (
        <div className="rounded-lg border border-amber-400/15 bg-amber-400/[0.05] px-3 py-2 text-[11px] text-amber-200/90">
          Two-factor sign-in is required for this platform. This account has {passkeys === 1 ? 'a passkey' : `${passkeys} passkeys`},
          which is its second factor: sign in with it, because a password alone is not accepted. You can also
          set up an authenticator app here.
        </div>
      ) : status.required_by_policy ? (
        <div className="rounded-lg border border-amber-400/15 bg-amber-400/[0.05] px-3 py-2 text-[11px] text-amber-200/90">
          Two-factor sign-in is required for this platform
          {status.enabled ? ', so it cannot be turned off here.' : '. Set it up now; you will also be asked at your next sign-in.'}
        </div>
      ) : null}
      {!status.enabled && !status.required_by_policy && passkeys > 0 ? (
        <p className="text-[10px] text-stone-500">
          {passkeys === 1 ? 'A passkey is' : `${passkeys} passkeys are`} registered on this account and count as a
          second factor when you sign in with {passkeys === 1 ? 'it' : 'one'}.
        </p>
      ) : null}

      {message ? (
        <div className="flex items-center gap-2 text-[11px] text-emerald-400">
          <CheckCircle2 size={12} /> {message}
        </div>
      ) : null}
      {error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-300">
          <AlertCircle size={13} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      ) : null}

      {mode === 'idle' ? (
        <div className="flex flex-wrap items-center gap-2">
          {!status.enabled ? (
            <button
              type="button"
              className="btn-primary text-[12px]"
              onClick={() => {
                setMessage(null)
                setError(null)
                setMode('enrol')
              }}
            >
              {status.pending ? 'Continue set-up' : 'Set up authenticator'}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn-secondary text-[12px]"
                onClick={() => {
                  setMessage(null)
                  setMode('rekey-code')
                }}
              >
                Replace authenticator
              </button>
              {!status.required_by_policy ? (
                <button
                  type="button"
                  className="text-[11px] text-stone-400 underline-offset-2 hover:text-stone-200 hover:underline"
                  onClick={() => {
                    setMessage(null)
                    setMode('disable')
                  }}
                >
                  Turn off
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {mode === 'enrol' ? (
        <TotpEnrolmentFlow
          client={client}
          onDone={() => void finished('Authenticator confirmed. Your next sign-in will ask for a code.')}
          onCancel={back}
          intro="You will scan a QR code with an authenticator app, then enter the code it shows. Two-factor stays off until you do."
        />
      ) : null}

      {mode === 'rekey-code'
        ? codeStep(
            'Replacing the authenticator needs a code from the one you have now. It keeps working until you confirm the new one.',
            () => setMode('rekey'),
            'Continue',
          )
        : null}

      {mode === 'rekey' ? (
        <TotpEnrolmentFlow
          client={client}
          currentCode={currentCode}
          autoBegin
          onStartFailed={(reason) => {
            setError(reason)
            setCurrentCode('')
            setMode('rekey-code')
          }}
          onDone={() => void finished('New authenticator confirmed. Your next sign-in will ask for a code from it.')}
          onCancel={back}
        />
      ) : null}

      {mode === 'disable'
        ? codeStep('Turning it off needs a code from your current authenticator.', () => void disable(), 'Turn off two-factor')
        : null}
    </div>
  )
}

/**
 * The trial-license "require 2FA" switch. It can only be ticked for a license
 * whose holder has already enrolled: the server refuses it otherwise, because
 * requiring a second factor nobody holds locks the holder out.
 */
export function TrialTwoFactorToggle({
  checked,
  enrolled,
  onChange,
}: {
  checked: boolean
  enrolled: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2 text-[11px] text-stone-300">
        <input
          aria-label="Require authenticator-based 2FA"
          checked={checked}
          className="h-4 w-4 rounded border-amber-300/30 bg-black/60 accent-amber-400 disabled:opacity-40"
          disabled={!enrolled}
          onChange={(event) => enrolled && onChange(event.target.checked)}
          type="checkbox"
        />
        Require authenticator-based 2FA
        {enrolled ? <span className="ml-auto text-[10px] text-emerald-400">Configured</span> : null}
      </label>
      {!enrolled ? (
        <p className="px-1 text-[10px] leading-relaxed text-stone-500">
          No authenticator is enrolled for this license, so 2FA cannot be required yet - it would lock the holder
          out. They can set one up under Settings &gt; Security.
        </p>
      ) : null}
    </div>
  )
}
